import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FileCheck, BadgeCheck, Printer, Info, Check } from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'


const APC_RATES = [
    { who: 'Indian Authors', amount: '₹4,500', unit: 'INR · per article', note: 'Effective 1 March 2026' },
    { who: 'International Authors', amount: '$75', unit: 'USD · per article', note: undefined }
]

const COVERS = [
    'Hosting and system maintenance',
    'Typesetting and copy-editing',
    'Printing and production',
    'Open-access availability',
    'One hard copy to the corresponding author'
]

export default function APC() {
    return (
        <>
            <SEO
                title="Article Processing Charge"
                description="Article Processing Charges (APC) for the Indian Studies Review — no submission fee; ₹4,500 (INR) for Indian authors and $75 (USD) for international authors, applied after acceptance."
                path="/article-processing-charge"
            />
            <PageHeader
                eyebrow="For Authors"
                title="Article Processing Charge"
                lede="There is no submission fee. A processing charge applies only after an article is accepted for publication."
            />

            <section className="section">
                <div className="container-prose">
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-5">
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="p-7 flex flex-col"
                            style={{ border: '1px solid var(--color-rule-strong)', background: 'var(--color-paper-50)' }}
                        >
                            <FileCheck size={26} style={{ color: 'var(--color-gold-700)' }} />
                            <div
                                className="mt-4 text-[11px] uppercase font-semibold"
                                style={{ letterSpacing: '0.16em', color: 'var(--color-ink-400)' }}
                            >
                                Article Submission Fee
                            </div>
                            <div
                                className="font-display mt-2"
                                style={{ fontSize: 'clamp(2rem, 5vw, 2.8rem)', color: 'var(--color-ink-700)', lineHeight: 1 }}
                            >
                                ₹0.00
                            </div>
                            <p className="mt-3 text-[14px]" style={{ color: 'var(--color-ink-500)' }}>
                                There is no fee to submit a manuscript for consideration.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.06 }}
                            className="p-7"
                            style={{ background: 'var(--color-gold-800)', color: 'var(--color-paper-50)' }}
                        >
                            <div className="flex items-center gap-2.5">
                                <BadgeCheck size={22} style={{ color: 'var(--color-gold-300)' }} />
                                <div
                                    className="text-[11px] uppercase font-semibold"
                                    style={{ letterSpacing: '0.16em', color: 'var(--color-gold-300)' }}
                                >
                                    Article Processing Fee (APC)
                                </div>
                            </div>
                            <p className="mt-2 text-[13.5px]" style={{ color: 'rgba(243,236,217,0.7)' }}>
                                Applies only after article acceptance.
                            </p>
                            <div className="mt-5 grid sm:grid-cols-2 gap-px" style={{ background: 'rgba(243,236,217,0.16)' }}>
                                {APC_RATES.map((r) => (
                                    <div key={r.who} className="p-5" style={{ background: 'var(--color-gold-800)' }}>
                                        <div className="text-[12.5px]" style={{ color: 'rgba(243,236,217,0.7)' }}>
                                            {r.who}
                                        </div>
                                        <div className="font-display mt-1" style={{ fontSize: '2rem', lineHeight: 1.05, color: 'var(--color-gold-300)' }}>
                                            {r.amount}
                                        </div>
                                        <div
                                            className="mt-1 text-[10.5px] uppercase font-semibold"
                                            style={{ letterSpacing: '0.12em', color: 'rgba(243,236,217,0.55)' }}
                                        >
                                            {r.unit}
                                        </div>
                                        {r.note && (
                                            <div className="mt-2 text-[11.5px]" style={{ color: 'var(--color-gold-300)' }}>
                                                {r.note}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-5 p-7"
                        style={{ border: '1px solid var(--color-rule)', background: 'var(--color-paper-50)' }}
                    >
                        <div className="kicker" style={{ margin: 0 }}>What the fee covers</div>
                        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">
                            {COVERS.map((c) => (
                                <div
                                    key={c}
                                    className="flex gap-3 items-start py-2.5"
                                    style={{ borderBottom: '1px solid var(--color-rule)' }}
                                >
                                    <span
                                        className="inline-flex items-center justify-center flex-shrink-0 mt-0.5"
                                        style={{ width: '1.3rem', height: '1.3rem', background: 'var(--color-gold-700)', color: 'white', borderRadius: '50%' }}
                                    >
                                        <Check size={13} strokeWidth={3} />
                                    </span>
                                    <span className="text-[14.5px]" style={{ color: 'var(--color-ink-600)' }}>
                                        {c}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    <div className="mt-5 grid lg:grid-cols-2 gap-5">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="p-7"
                            style={{ border: '1px solid var(--color-rule-strong)', background: 'var(--color-paper-50)' }}
                        >
                            <Printer size={24} style={{ color: 'var(--color-gold-700)' }} />
                            <h3 className="font-display mt-4" style={{ fontSize: '1.2rem', color: 'var(--color-ink-700)' }}>
                                Print Copy Charges
                            </h3>
                            <p className="mt-2 text-[14.5px]" style={{ color: 'var(--color-ink-500)' }}>
                                Additional hard copies are available upon request.
                            </p>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-display" style={{ fontSize: '1.6rem', color: 'var(--color-ink-700)' }}>
                                    ₹500
                                </span>
                                <span className="text-[13px]" style={{ color: 'var(--color-ink-400)' }}>
                                    per copy · postage within India included
                                </span>
                            </div>
                            <div
                                className="mt-4 px-4 py-3 text-[13px]"
                                style={{ background: 'var(--color-paper-100)', borderLeft: '3px solid var(--color-accent-500)', borderRadius: '2px', color: 'var(--color-ink-600)' }}
                            >
                                <strong>Important:</strong> International shipping is not available.
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.06 }}
                            className="p-7"
                            style={{ background: 'var(--color-paper-100)', borderLeft: '3px solid var(--color-gold-500)', borderRadius: '2px' }}
                        >
                            <div className="flex items-center gap-2.5">
                                <Info size={20} style={{ color: 'var(--color-gold-700)' }} />
                                <div className="font-display" style={{ fontSize: '1.1rem', color: 'var(--color-ink-700)' }}>
                                    Note on the revised charges
                                </div>
                            </div>
                            <p className="mt-3 text-[14.5px] leading-relaxed" style={{ color: 'var(--color-ink-600)' }}>
                                CSPG has revised the Article Processing Charges due to increased costs
                                related to hosting, system maintenance, typesetting (copy-editing) and
                                printing. Under the revised policy, the APC and subscription charges
                                above shall be levied, and one hard copy of the published article shall
                                be provided to the corresponding author. This fee covers publication
                                costs and ensures open-access availability.
                            </p>
                        </motion.div>
                    </div>

                    <div
                        className="mt-12 flex flex-wrap items-center justify-between gap-5 pt-9"
                        style={{ borderTop: '1px solid var(--color-rule-strong)' }}
                    >
                        <p className="lede" style={{ maxWidth: '32rem', fontSize: '1.05rem' }}>
                            Questions about charges, or ready to submit your manuscript?
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/subscription" className="btn btn-ghost">
                                Subscription
                            </Link>
                            <Link to="/submission" className="btn btn-gold">
                                Make a submission →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
